import {
  AtSign,
  FileText,
  Hand,
  LayoutTemplate,
  MousePointer2,
  Pencil,
  Plus,
  Smile,
  SquareMousePointer,
  StickyNote,
  Type,
  type LucideIcon,
} from "lucide-react";

/**
 * The commanding bar, left to right, grouped 1 · 3 · 5 · 1
 * (exp-canvas-toolbar `tokens.js` GROUPS). Cursor and Shapes wear their
 * current pick rather than a fixed glyph; Blocks takes LayoutTemplate (the
 * settled pick); the Stickers tile keeps the spec's Smile under the product's
 * narrower name (DECISIONS D10).
 */
export type CanvasToolKey =
  | "cursor"
  | "sticky"
  | "doc"
  | "blocks"
  | "section"
  | "text"
  | "shapes"
  | "connectors"
  | "stickers"
  | "insert";

export type CanvasToolGroupKey = "cursor" | "capture" | "board" | "insert";

export const CANVAS_TOOLBAR_GROUPS: {
  key: CanvasToolGroupKey;
  tools: CanvasToolKey[];
}[] = [
  { key: "cursor", tools: ["cursor"] },
  { key: "capture", tools: ["sticky", "doc", "blocks"] },
  {
    key: "board",
    tools: ["section", "text", "shapes", "connectors", "stickers"],
  },
  { key: "insert", tools: ["insert"] },
];

export const CANVAS_TOOL_LABELS: Record<CanvasToolKey, string> = {
  cursor: "Cursor",
  sticky: "Sticky note",
  doc: "Doc",
  blocks: "Blocks",
  section: "Section",
  text: "Text · Label",
  shapes: "Shapes",
  connectors: "Connectors",
  stickers: "Stickers",
  insert: "Insert",
};

/** Fixed glyphs; Cursor and Shapes resolve theirs from the current pick. */
export const CANVAS_TOOL_ICONS: Partial<Record<CanvasToolKey, LucideIcon>> = {
  sticky: StickyNote,
  doc: FileText,
  blocks: LayoutTemplate,
  section: SquareMousePointer,
  text: Type,
  connectors: AtSign,
  stickers: Smile,
  insert: Plus,
};

export type CanvasCursorMode = "pointer" | "hand" | "draw";

export const CANVAS_CURSOR_MODES: {
  key: CanvasCursorMode;
  label: string;
  icon: LucideIcon;
  /** The key that arms it, shown in its tooltip (ROUND3 §4). */
  hint?: string;
}[] = [
  { key: "pointer", label: "Pointer", icon: MousePointer2 },
  { key: "hand", label: "Hand", icon: Hand, hint: "H" },
  // The pencil, not the sheet's PenTool (toolbar.jsx CURSOR_ICONS): Joey's
  // call, ROUND5 §3, D48. The tray's pen tip keeps lucide Pen.
  { key: "draw", label: "Draw", icon: Pencil },
];

/** Lucide takes sizes as props: 20 on a tile, 16 in menus and chrome, both at
 * the registry's stroke. */
export const CANVAS_TILE_GLYPH = 20;
export const CANVAS_MENU_GLYPH = 16;
export const CANVAS_GLYPH_STROKE = 1.75;
